import React, { useRef, useEffect } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, api } from "@/lib/api";
import AssistantWidget from "@/components/AssistantWidget";
import NotificationBell from "@/components/NotificationBell";
import AnnouncementBar from "@/components/chat/AnnouncementBar";
import {
  LayoutDashboard, Users, Briefcase, FileSignature, Ban, KeyRound,
  Landmark, Scale, FileText, Wallet, Mail, UserCog, LogOut, Building2, BarChart3,
  Globe, Cloud, MailCheck, UserCircle, Sparkles, Recycle, CalendarClock, MessageSquare, Menu, PhoneCall,
} from "lucide-react";

const NAV_ITEMS = [
  { section: "Communication", items: [
    { to: "/chat", label: "Chat", icon: MessageSquare, roles: ["admin","dirigeant","manager_commercial","vendeur","secretaire_bo","responsable_facturation"], testid: "nav-chat", badgeKey: "chat" },
  ]},
  { section: "Espace Commerce", items: [
    { to: "/commerce", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin","dirigeant","manager_commercial","vendeur"], testid: "nav-dashboard-commerce" },
    { to: "/prospects", label: "Prospects", icon: Users, roles: ["admin","dirigeant","manager_commercial","vendeur"], testid: "nav-prospects" },
    { to: "/rdv-planifies", label: "RDV planifiés", icon: CalendarClock, roles: ["admin","dirigeant","manager_commercial","vendeur"], testid: "nav-rdv-planifies" },
    { to: "/relances", label: "Relances", icon: PhoneCall, roles: ["admin","dirigeant","manager_commercial","vendeur"], testid: "nav-relances" },
    { to: "/pot-commun", label: "Pot commun", icon: Recycle, roles: ["admin","dirigeant","manager_commercial","vendeur"], testid: "nav-pot-commun", needsPotCommun: true },
    { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["admin","dirigeant","manager_commercial","vendeur"], testid: "nav-analytics" },
  ]},
  { section: "Espace Admin / BackOffice", items: [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin","dirigeant","manager_commercial","secretaire_bo","responsable_facturation"], testid: "nav-dashboard-admin" },
    { to: "/dossiers", label: "Dossiers signés", icon: Briefcase, roles: ["admin","dirigeant","manager_commercial","secretaire_bo","responsable_facturation","vendeur"], testid: "nav-dossiers" },
    { to: "/denonciations", label: "Dénonciations", icon: Ban, roles: ["admin","dirigeant","secretaire_bo"], testid: "nav-denonciations" },
    { to: "/options", label: "Options", icon: KeyRound, roles: ["admin","dirigeant","secretaire_bo"], testid: "nav-options" },
    { to: "/financements", label: "Financements", icon: Landmark, roles: ["admin","dirigeant","secretaire_bo"], testid: "nav-financements" },
    { to: "/notaire", label: "Passages notaire", icon: Scale, roles: ["admin","dirigeant","secretaire_bo"], testid: "nav-notaire" },
    { to: "/factures", label: "Factures", icon: FileText, roles: ["admin","dirigeant","responsable_facturation"], testid: "nav-factures" },
    { to: "/recouvrement", label: "Recouvrement", icon: Wallet, roles: ["admin","dirigeant","responsable_facturation"], testid: "nav-recouvrement" },
    { to: "/validation-emails", label: "Validation emails", icon: MailCheck, roles: ["admin","dirigeant","secretaire_bo","responsable_facturation","manager_commercial"], testid: "nav-validation-emails" },
    { to: "/historique-emails", label: "Historique emails", icon: Mail, roles: ["admin","dirigeant","secretaire_bo","responsable_facturation","manager_commercial"], testid: "nav-historique-emails" },
    { href: "https://le-kiosque-immobilier.link-app.immo/login", label: "Plateforme", icon: Globe, roles: ["admin","dirigeant","manager_commercial","vendeur","secretaire_bo","responsable_facturation"], testid: "nav-plateforme", external: true },
    { href: "https://cloud.lekiosqueimmobilier.fr/", label: "Cloud", icon: Cloud, roles: ["admin","dirigeant","manager_commercial","vendeur","secretaire_bo","responsable_facturation"], testid: "nav-cloud", external: true },
    { to: "/portails-promoteurs", label: "Promoteurs", icon: KeyRound, roles: ["admin","dirigeant","manager_commercial","vendeur","secretaire_bo","responsable_facturation"], testid: "nav-portails-promoteurs" },
  ]},
  { section: "Configuration", items: [
    { to: "/reports", label: "Rapports & Intégrations", icon: BarChart3, roles: ["admin","dirigeant","manager_commercial"], testid: "nav-reports" },
    { to: "/suck-my-lead", label: "Suck My Lead", icon: Cloud, roles: ["admin","dirigeant","manager_commercial"], testid: "nav-suck-my-lead" },
    { to: "/demandes-ia", label: "Demandes IA", icon: Sparkles, roles: ["admin","dirigeant","manager_commercial"], testid: "nav-demandes-ia" },
    { to: "/promoteurs", label: "Promoteurs", icon: Building2, roles: ["admin","dirigeant","secretaire_bo","responsable_facturation","manager_commercial"], testid: "nav-promoteurs" },
    { to: "/templates", label: "Modèles emails", icon: Mail, roles: ["admin","dirigeant","secretaire_bo","responsable_facturation"], testid: "nav-templates" },
    { to: "/users", label: "Utilisateurs", icon: UserCog, roles: ["admin","dirigeant"], testid: "nav-users" },
  ]},
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const assistantRef = useRef(null);
  const isChat = location.pathname.startsWith("/chat");
  const [potCommunEnabled, setPotCommunEnabled] = React.useState(false);
  const [chatMentions, setChatMentions] = React.useState(0);
  const [mobileNav, setMobileNav] = React.useState(false);

  useEffect(() => { setMobileNav(false); }, [location.pathname]);

  useEffect(() => {
    api.get("/settings/pot-commun").then((r) => setPotCommunEnabled(r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    const load = () => api.get("/chat/badge").then((r) => setChatMentions(r.data.mentions || 0)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      assistantRef.current?.openWithContext(e.detail || {});
    };
    window.addEventListener("open-assistant", handler);
    return () => window.removeEventListener("open-assistant", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-[#111111] text-white z-30 flex items-center gap-3 px-4" data-testid="mobile-topbar">
        <button onClick={() => setMobileNav(true)} data-testid="mobile-nav-toggle" className="p-1 -ml-1 text-white">
          <Menu className="w-6 h-6" strokeWidth={1.75} />
        </button>
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#B8873A]" strokeWidth={1.5} />
          <span className="font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk" }}>Le Kiosque Immobilier</span>
        </div>
      </div>

      {/* Mobile drawer overlay */}
      {mobileNav && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileNav(false)} data-testid="mobile-nav-overlay" />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#111111] text-white flex flex-col shrink-0 transform transition-transform duration-200 ${mobileNav ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="sidebar">
        <div className="px-6 py-6 border-b border-slate-700/40">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#B8873A]" strokeWidth={1.5} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Le Kiosque</div>
              <div className="font-bold text-lg tracking-tight" style={{fontFamily:"Cabinet Grotesk"}}>Immobilier</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {NAV_ITEMS.map((section) => {
            const visible = section.items.filter((it) => it.roles.includes(user?.role) && (!it.needsPotCommun || potCommunEnabled));
            if (visible.length === 0) return null;
            return (
              <div key={section.section} className="mb-4">
                <div className="px-6 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                  {section.section}
                </div>
                {visible.map((item) => (
                  item.external ? (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={item.testid}
                      className="sidebar-link"
                    >
                      <item.icon className="w-4 h-4" strokeWidth={1.5} />
                      <span>{item.label}</span>
                    </a>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={item.testid}
                      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                    >
                      <item.icon className="w-4 h-4" strokeWidth={1.5} />
                      <span>{item.label}</span>
                      {item.badgeKey === "chat" && chatMentions > 0 && (
                        <span data-testid="nav-chat-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                          {chatMentions > 9 ? "9+" : chatMentions}
                        </span>
                      )}
                    </NavLink>
                  )
                ))}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-700/40 p-4">
          <div className="mb-3">
            <div className="text-sm font-semibold text-white">{user?.name}</div>
            <div className="text-xs text-slate-400">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          <NavLink
            to="/mon-compte"
            data-testid="nav-mon-compte"
            className={({ isActive }) => `w-full flex items-center gap-2 px-3 py-2 mb-1 text-sm rounded-sm transition-colors ${isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:text-white hover:bg-slate-800"}`}
          >
            <UserCircle className="w-4 h-4" strokeWidth={1.5} />
            Mon compte
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 lg:pr-16 flex flex-col overflow-hidden pt-14 lg:pt-0">
        <AnnouncementBar />
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Outlet />
        </div>
      </main>

      <NotificationBell />

      {/* Floating AI Assistant available on all pages except Chat (overlaps composer) */}
      {!isChat && <AssistantWidget ref={assistantRef} />}
    </div>
  );
}
